import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button, Description, Drawer, type OverlayTriggerState } from '@heroui/react';
import type { ParkingSpotOwner, SpotUsage } from '../../../api/my-parking-spots';
import { AsyncButton } from '../../../components/ui/async-button';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../components/ui/side-drawer-classes';
import { UsageTypeChip } from '../../../components/ui/usage-chips';
import { formatRequesterLabel, formatUsagePeriod } from '../utils/usage-display';

interface PendingRequestsDrawerProps {
  state: OverlayTriggerState;
  spot: ParkingSpotOwner | null;
  requests: SpotUsage[];
  highlightedUsageId: string | null;
  onApprove: (usageId: string) => Promise<void>;
  onReject: (usageId: string) => Promise<void>;
}

export function PendingRequestsDrawer({
  state,
  spot,
  requests,
  highlightedUsageId,
  onApprove,
  onReject,
}: PendingRequestsDrawerProps) {
  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>
                {spot ? `Solicitações · ${spot.spotCode}` : 'Solicitações'}
              </Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={drawerBodyClassName}>
              {spot ? (
                <Description>Aprove ou rejeite pedidos de uso da sua vaga.</Description>
              ) : null}

              {requests.length === 0 ? (
                <Description className="py-8 text-center">Nenhuma solicitação pendente.</Description>
              ) : (
                requests.map((request) => (
                  <RequestRow
                    key={request.id}
                    request={request}
                    highlighted={highlightedUsageId === request.id}
                    onApprove={() => onApprove(request.id)}
                    onReject={() => onReject(request.id)}
                  />
                ))
              )}
            </Drawer.Body>

            <Drawer.Footer className={`${drawerFooterClassName} !justify-stretch`}>
              <Button variant="secondary" className="w-full" onPress={() => state.close()}>
                Fechar
              </Button>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}

function RequestRow({
  request,
  highlighted,
  onApprove,
  onReject,
}: {
  request: SpotUsage;
  highlighted: boolean;
  onApprove: () => Promise<void>;
  onReject: () => Promise<void>;
}) {
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);

  return (
    <div
      className={`rounded-lg border bg-surface p-4 ${
        highlighted ? 'border-accent ring-2 ring-accent/20' : 'border-border-secondary'
      }`}
    >
      <p className="font-medium">{formatRequesterLabel(request)}</p>
      <p className="mt-1 text-sm text-muted">{formatUsagePeriod(request.startAt, request.endAt)}</p>
      <UsageTypeChip type={request.type} className="mt-2" />
      <div className="mt-3 flex gap-2">
        <AsyncButton
          size="sm"
          variant="primary"
          className="flex-1"
          isPending={action === 'approve'}
          isDisabled={action === 'reject'}
          pendingLabel="Aprovar"
          onPress={async () => {
            setAction('approve');
            try {
              await onApprove();
            } finally {
              setAction(null);
            }
          }}
        >
          <Check size={14} />
          Aprovar
        </AsyncButton>
        <AsyncButton
          size="sm"
          variant="danger-soft"
          className="flex-1"
          isPending={action === 'reject'}
          isDisabled={action === 'approve'}
          pendingLabel="Rejeitar"
          onPress={async () => {
            setAction('reject');
            try {
              await onReject();
            } finally {
              setAction(null);
            }
          }}
        >
          <X size={14} />
          Rejeitar
        </AsyncButton>
      </div>
    </div>
  );
}
